<template>
  <div
    class="fts-table-edit-display-container"
    :class="{'fts-scrollbar': overflowAuto}"
    ref="ftsTableEditDisplay"
  >
    <table
      class="fts-table-edit fts-table-edit-display"
      :class="{ 'table-edit--sticky-header': stickyHeader }"
    >
      <tr ref="headerRow">
        <th
          v-for="(column, columnIndex) in getColumns"
          :key="column.id"
          :style="frozenColumnStyle(columnIndex) || getColumnWidth(column)"
          :class="frozenColumnClass(columnIndex)"
          @mouseenter="measureFreezeGeometry"
          @focusin="measureFreezeGeometry"
          :data-freeze-column="columnIndex"
        >
          <div class="col-label" :class="colLabelClass(columnIndex)">
            <div class="col-label-name">{{getColumnLabel(column)}}</div>
            <i
              class="col-label-icon"
              :class="getIcon(column.type)"
              v-if="hideLabelIcon !== true"
            ></i>
            <!-- freeze columns (SS05971) — see FTSTableEdit for why the trigger is a
                 button and why the click handler sits on the root -->
            <fts-tippy
              class="col-freeze"
              trigger-tag="button"
              append-trigger-class="col-freeze-toggle"
              :class="[
                {'col-freeze--active': isColumnFrozen(columnIndex)},
                {'col-freeze--disabled': !isColumnFrozen(columnIndex) && !freezeAllowed(columnIndex)}
              ]"
              v-if="freezeEnabled"
              @click.native.stop.prevent="toggleFreeze(columnIndex)"
            >
              <!-- see FTSTableEdit — both glyphs rendered, cross-faded in CSS -->
              <span class="material-symbols-outlined col-freeze-icon" aria-hidden="true">keep</span>
              <span class="material-symbols-outlined col-freeze-icon col-freeze-icon--off" aria-hidden="true">keep_off</span>
              <!-- see FTSTableEdit: a `title` attribute would add a second native tooltip -->
              <span class="col-freeze-label">{{freezeHint(columnIndex)}}</span>
              <div slot="content" class="tippy-hint">{{freezeHint(columnIndex)}}</div>
            </fts-tippy>
          </div>
        </th>
      </tr>
      <tr v-for="item in formData" :key="item.id">
        <td
          v-for="(column, columnIndex) in getColumns"
          :key="column.id"
          :style="frozenColumnStyle(columnIndex) || getColumnWidth(column)"
          :class="frozenColumnClass(columnIndex)"
        >
          <fts-display
            :item="column"
            :form-data="item"   
            :vector-id="vectorId"
            :view="view"
            :share-form="shareForm"
            in-table
            class="form-display-borderless" 
            :class="getElementClass(column)"
          />
        </td>
      </tr>
    </table>
  </div>
</template>

<script>
import commons from "../commons/commons";
import tableFreeze from "../commons/tableFreeze";
import LocalStorageService from "../store/LocalStorageService";
import FTSTippy from "./FTSTippy";

export default {
  components: {
    "fts-tippy": FTSTippy
  },
  props: {
    formData: Array,
    item: Object,
    vectorId: [ Number, String ],
    /* form identity for the per-form freeze preference (SS05971); absent on a view form */
    activityId: String,
    /* see FTSTableEdit — the form this table's freeze preference belongs to, named by the
       host through formSettings.tableFreezeFormKey rather than derived here */
    formKey: String,
    comfortDesign: Boolean,
    overflowAuto: Boolean,
    view: Boolean,
    shareForm: Boolean,
    stickyHeader: Boolean,
    hideLabelIcon: Boolean
  },
  data: () => ({
    /* freeze columns (SS05971) — fieldNames, not indices, so a design change cannot
       silently freeze the wrong columns */
    frozenFieldNames: [],
    /* visible width of the horizontal scrollport — see FTSTableEdit */
    freezeContainerWidth: 0,
    /* fieldName → rendered px, captured while still unfrozen — see FTSTableEdit */
    frozenColumnWidths: {},
    pendingFrozenFieldNames: null
  }),
  /* see FTSTableEdit — all freeze setup is gated so it costs nothing on the surfaces
     where the feature cannot activate */
  created() {
    if (this.freezeEnabled) this.loadFreezePreference();
  },
  mounted() {
    if (!this.freezeEnabled) return;
    /* see FTSTableEdit — the widths as well as the scrollport, so a pin is correctly enabled
       or disabled on the first paint rather than only after the first hover */
    this.measureFreezeGeometry();
    /* see FTSTableEdit — a preference loaded in created() waits for a measured table */
    this.applyPendingFreeze();
    window.addEventListener('resize', this.measureFreezeContainer);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measureFreezeContainer);
  },
  watch: {
    /* see FTSTableEdit — the indicator renders in the field's label row, two components up, so
       the summary is published rather than rendered here. `immediate` covers a preference
       restored on mount, where no toggle ever fires. */
    frozenColumnSummary: {
      immediate: true,
      handler(summary) {
        this.$emit('freeze-summary', summary);
      }
    },
    /* see FTSTableEdit — keeps the loaded preference in step with the storage key
       instead of relying on the caller to remount per activity. Both key sources are
       watched because freezeFormKey() reads both. */
    formKey() {
      if (this.freezeEnabled) this.loadFreezePreference();
    },
    activityId() {
      if (this.freezeEnabled) this.loadFreezePreference();
    },
    'item.id'() {
      if (this.freezeEnabled) this.loadFreezePreference();
    },
    /* see FTSTableEdit — covers a freezeEnabled that only becomes true after created() */
    freezeEnabled(enabled) {
      if (enabled) {
        this.loadFreezePreference();
        this.measureFreezeGeometry();
        window.addEventListener('resize', this.measureFreezeContainer);
      } else {
        window.removeEventListener('resize', this.measureFreezeContainer);
      }
    }
  },
  methods: {
    getDisplayClass(item) {
      if (
        item.input == 'boolean' ||
        item.input == 'toggle' ||
        item.type === 1
      ) {
        return "table-display-boolean";
      } else if (
        item.input == 'integer' ||
        item.type === 2
      ) {
        return "table-display-integer";
      } else if (
        item.input == 'number' ||
        item.type === 3 ||
        item.type === 4
      ) {
        return "table-display-number";
      } else if (
        item.input === 'imageUpload' ||
        item.input === 'imagesUpload' ||
        item.input === 'fileUpload' ||
        item.input === 'filesUpload' ||
        item.type === 10 ||
        item.type === 11 ||
        item.type === 12 ||
        item.type === 13
      ) {
        return "table-display-attachment";
      } else if (
        item.input == 'signature' ||
        item.type === 29
      ) {
        return "table-display-signature";
      }
    },
    getElementClass(item) {
      if (item.input == 'boolean' || item.input == 'toggle' || item.type === 1) {
        return "te-yes-no-display";
      } else if (item.input == 'integer' || item.type === 2) {
        return "te-integer-display";
      } else if (item.input == 'number' || item.type === 3 || item.type === 4 ) {
        return "te-number-currency-display";
      } else if (!this.shareForm && (item.input == 'signature' || item.type === 29)) {
        return "te-signature-display";
      }
    },
    getColumnWidth(item) {
      let minWidth = 150 //default minWidth
      if (item.columnWidth) {
        return {
          'width': item.columnWidth + "px",
          'minWidth': item.columnWidth + "px",
          'max-width': item.columnWidth + "px"
        }
        // minWidth = item.columnWidth
      } else if (item.display || item.input === 'display' || item.readOnly) {
        minWidth = 150
      } else if (item.input === 'imageUpload' || item.input === 'imagesUpload' || item.input === 'fileUpload' || item.input === 'filesUpload') {
        minWidth = 250
      } else if (item.input === 'boolean') {
        minWidth = 50
      } 
      return "minWidth:" + minWidth + "px"
    },
    getColumnLabel(item) {
      return item.label || item.displayName;
    },
    getIcon(typeId) {
      return commons.informationIcon(typeId);
    },
    /* freeze columns (SS05971) */
    freezeStorageKey() {
      return this.item && this.item.id;
    },
    /* see FTSTableEdit — never defaulted to 'view', because a key that is merely absent must
       not be mistaken for the entry view form's key and paint that form's columns frozen
       @returns {string|null} */
    freezeFormKey() {
      return this.formKey || this.activityId || null;
    },
    /* see FTSTableEdit — loaded here, applied only once the unfrozen table has been
       measured, so a stored freeze never pins fallback widths on the first paint */
    loadFreezePreference() {
      /* cleared first and unconditionally — see FTSTableEdit */
      this.frozenColumnWidths = {};
      this.frozenFieldNames = [];
      this.pendingFrozenFieldNames = null;

      const fieldId = this.freezeStorageKey();
      const formKey = this.freezeFormKey();
      if (!fieldId || !formKey) return;

      const preference = LocalStorageService.getTableFreezePreference(
        this.vectorId, formKey, fieldId
      );
      const columns = (preference && Array.isArray(preference.columns))
        ? preference.columns
        : [];

      this.pendingFrozenFieldNames = columns.length ? columns : null;

      if (this.pendingFrozenFieldNames) this.$nextTick(this.applyPendingFreeze);
    },
    applyPendingFreeze() {
      if (!this.freezeEnabled || !this.pendingFrozenFieldNames) return;
      if (!this.measureColumnWidths()) return;

      this.frozenFieldNames = this.pendingFrozenFieldNames;
      this.pendingFrozenFieldNames = null;
    },
    /* see FTSTableEdit for why this reads getBoundingClientRect, why it is
       hover-independent, and what `preserveFrozen` protects. This component renders bare
       <tr> children of <table> with no thead, so the header row is resolved through its own
       ref rather than through `rows[0]` of a section that does not exist. There is no
       row-action column here, so the header cells align with `getColumns` one to one.
       @param {Object} [options]
       @param {boolean} [options.preserveFrozen] keep the captured width of every currently
         frozen column and re-read only the rest
       @returns {boolean} whether a measurement was actually taken */
    measureColumnWidths(options) {
      if (!this.freezeEnabled) return false;

      const preserveFrozen = Boolean(options && options.preserveFrozen);
      const headerRow = this.$refs.headerRow;
      if (!headerRow || !headerRow.cells || !headerRow.cells.length) return false;

      const cells = headerRow.cells;
      const widths = preserveFrozen ? Object.assign({}, this.frozenColumnWidths) : {};

      this.getColumns.forEach((column, index) => {
        const cell = cells[index];
        if (!column || !column.fieldName || !cell) return;
        if (preserveFrozen && this.isColumnFrozen(index)) return;

        const width = cell.getBoundingClientRect().width;
        if (width > 0) widths[column.fieldName] = width;
      });

      this.frozenColumnWidths = widths;
      return true;
    },
    /* see FTSTableEdit — the horizontal scrollport is an ancestor, not the table */
    freezeScrollport() {
      let el = this.$refs.ftsTableEditDisplay;
      while (el && el !== document.body) {
        const overflowX = window.getComputedStyle(el).overflowX;
        if (overflowX === 'auto' || overflowX === 'scroll') return el;
        el = el.parentElement;
      }
      return null;
    },
    /* see FTSTableEdit — window resize only; mount, hover and focus go through
       measureFreezeGeometry below */
    measureFreezeContainer() {
      /* the th listeners are bound unconditionally, so guard the hot path */
      if (!this.freezeEnabled) return;

      const scrollport = this.freezeScrollport() || this.$refs.ftsTableEditDisplay;
      this.freezeContainerWidth = scrollport ? scrollport.clientWidth : 0;
    },
    /* see FTSTableEdit — the eligibility rule needs the rendered widths as well as the
       scrollport width, and both are read in the one pass the hover already pays for */
    measureFreezeGeometry() {
      if (!this.freezeEnabled) return;

      this.measureFreezeContainer();
      this.measureColumnWidths({ preserveFrozen: true });
    },
    /* see FTSTableEdit — the pin sits beside the label unless the column is too narrow for
       that, in which case it floats over it */
    colLabelClass(index) {
      if (!this.freezeEnabled) return null;

      const column = this.getColumns[index];
      const measured = column ? this.frozenColumnWidths[column.fieldName] : undefined;
      return tableFreeze.pinOverlayRequired(column, measured)
        ? 'col-label--freeze-overlay'
        : null;
    },
    isColumnFrozen(index) {
      return Boolean(this.frozenColumns[index]);
    },
    frozenColumnStyle(index) {
      const frozen = this.frozenColumns[index];
      if (!frozen) return null;
      return {
        left: `${frozen.left}px`,
        width: `${frozen.width}px`,
        'min-width': `${frozen.width}px`,
        'max-width': `${frozen.width}px`
      };
    },
    frozenColumnClass(index) {
      const frozen = this.frozenColumns[index];
      if (!frozen) return null;

      const classes = ['col-frozen'];
      if (frozen.isLast) classes.push('col-frozen--last');
      /* see FTSTableEdit — only a clamped column clips */
      if (frozen.clamped) classes.push('col-frozen--clamped');
      return classes;
    },
    /* see FTSTableEdit — one options object shared with the pinning path, so the budget can
       never be computed from widths the table is not rendering */
    freezeEligibilityOptions() {
      return Object.assign({}, this.frozenGeometryOptions, {
        frozenIndices: this.frozenIndices,
        containerWidth: this.freezeContainerWidth
      });
    },
    freezeAllowed(index) {
      return tableFreeze.freezeEligibility(
        this.getColumns, index, this.freezeEligibilityOptions()
      ).eligible;
    },
    freezeHint(index) {
      if (this.isColumnFrozen(index)) return 'Click to unfreeze this column';

      const eligibility = tableFreeze.freezeEligibility(
        this.getColumns, index, this.freezeEligibilityOptions()
      );

      if (!eligibility.eligible) {
        /* see FTSTableEdit — names the budget as well as the overflow */
        return 'This column can\'t be frozen — the frozen columns would need '
          + `${Math.round(eligibility.blockWidth)}px of the ${Math.round(eligibility.limit)}px `
          + 'available, leaving too little of the table to scroll.';
      }
      return 'Click to freeze this column';
    },
    toggleFreeze(index) {
      const fieldId = this.freezeStorageKey();
      const formKey = this.freezeFormKey();
      if (!fieldId || !formKey) return;
      /* unfreezing is always allowed, so this only blocks an ineligible new freeze */
      if (!this.freezeAllowed(index)) return;

      const columns = tableFreeze.toggleFrozenFieldName(
        this.getColumns, this.frozenFieldNames, index
      );

      /* see FTSTableEdit — measure before the assignment, so the widths captured are the
         ones currently on screen rather than the pins this is about to apply, and keep the
         already-frozen columns on their captured natural widths */
      this.measureColumnWidths({ preserveFrozen: true });

      this.frozenFieldNames = columns;
      LocalStorageService.setTableFreezePreference(
        this.vectorId, formKey, fieldId, { columns: columns }
      );
    },
    /* see FTSTableEdit — one write rather than n toggles for one user action (AC33) */
    unfreezeAllColumns() {
      const fieldId = this.freezeStorageKey();
      const formKey = this.freezeFormKey();
      if (!fieldId || !formKey) return;

      this.frozenFieldNames = [];
      LocalStorageService.setTableFreezePreference(
        this.vectorId, formKey, fieldId, { columns: [] }
      );
    },
    /* Scroll a frozen column to its own pin position (AC32) — see FTSTableEdit. No
       `columnScrollIntoView` to reuse here anyway; this component has no keyboard scroll (F7). */
    revealFrozenColumn(index) {
      const header = this.$el.querySelector(`th[data-freeze-column="${index}"]`);
      const scrollport = this.freezeScrollport();
      if (!header || !scrollport) return;

      const offset = header.getBoundingClientRect().left
        - scrollport.getBoundingClientRect().left;
      const pinned = parseFloat(window.getComputedStyle(header).left) || 0;
      if (offset > pinned) scrollport.scrollLeft += (offset - pinned);
    }
  },
  computed: {
    /* freeze columns (SS05971 D3, D39) — mirrors FTSTableEdit: the control only exists where
       the header is already pinned, because freezing needs the table to become its own
       scrollport and the header has to stay visible inside it. */
    freezeEnabled() {
      return Boolean(this.stickyHeader);
    },
    /* What the indicator lists (D35). `getColumnLabel` is this component's own label rule, and
       passing it in is what keeps the chip's labels identical to the header's. */
    frozenColumnSummary() {
      if (!this.freezeEnabled) return [];
      return tableFreeze.frozenSummary(
        this.getColumns, this.frozenIndices, column => this.getColumnLabel(column)
      );
    },
    frozenIndices() {
      if (!this.freezeEnabled) return [];
      return tableFreeze.resolveFrozenIndices(this.getColumns, this.frozenFieldNames);
    },
    /* see FTSTableEdit — shared by the geometry and the eligibility rule so they can never
       disagree about a width. There is no row-action column in view mode, so `hasRowAction`
       stays at its default. */
    frozenGeometryOptions() {
      return {
        measuredWidths: this.frozenColumnWidths
      };
    },
    frozenColumns() {
      return tableFreeze.buildFrozenColumns(
        this.getColumns, this.frozenIndices, this.frozenGeometryOptions
      );
    },
		getColumns() {
      if (!this.item.fields && this.item.columns)
        this.item.fields = this.item.columns

      // type 18 === input hidden
      return this.item.fields.filter(x => (x.type !== 18 || x.input != 'hidden') && !x.hide && x.visible !== false)
    }
	},
  beforeCreate: function () {
		this.$options.components.FtsDisplay = require('./FTSDisplay').default
	}
}
</script>